import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import {
  DEFAULT_SHORTCUTS, DEFAULT_SECTIONS, DEFAULT_TABS, MAX_CONFIGURABLE_TABS,
  MOBILE_SHORTCUTS, MOBILE_SECTIONS, MOBILE_TABS,
  shortcutByKey, sectionByKey, tabByKey,
  type MobileShortcut, type MobileSection, type MobileTab,
} from "@/config/mobileHomeConfig";

export interface MobileLayout {
  shortcuts: string[];
  sections: string[];
  bottomTabs: string[];
}

export const DEFAULT_LAYOUT: MobileLayout = {
  shortcuts: DEFAULT_SHORTCUTS,
  sections: DEFAULT_SECTIONS,
  bottomTabs: DEFAULT_TABS,
};

/** Keep only keys that exist in the registry; unknown/renamed keys are dropped. */
function sanitize(saved: unknown, valid: Set<string>, fallback: string[]): string[] {
  if (!Array.isArray(saved) || saved.length === 0) return fallback;
  const seen = new Set<string>();
  const out = saved.filter(
    (k): k is string => typeof k === "string" && valid.has(k) && !seen.has(k) && !!seen.add(k)
  );
  return out.length ? out : fallback;
}

export function useMobileLayout() {
  const { user } = useAuth();
  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["mobile-layout", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<MobileLayout> => {
      const { data, error } = await supabase
        .from("user_mobile_layouts")
        .select("shortcuts, sections, bottom_tabs")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return DEFAULT_LAYOUT;
      return {
        shortcuts: sanitize(data.shortcuts, new Set(MOBILE_SHORTCUTS.map((s) => s.key)), DEFAULT_SHORTCUTS),
        sections: sanitize(data.sections, new Set(MOBILE_SECTIONS.map((s) => s.key)), DEFAULT_SECTIONS),
        bottomTabs: sanitize(data.bottom_tabs, new Set(MOBILE_TABS.map((t) => t.key)), DEFAULT_TABS),
      };
    },
  });

  const layout = query.data ?? DEFAULT_LAYOUT;

  const saveLayout = useMutation({
    mutationFn: async (next: MobileLayout) => {
      if (!user?.id) throw new Error("Not signed in");
      const { error } = await supabase.from("user_mobile_layouts").upsert(
        {
          user_id: user.id,
          shortcuts: next.shortcuts,
          sections: next.sections,
          bottom_tabs: next.bottomTabs.slice(0, MAX_CONFIGURABLE_TABS),
        },
        { onConflict: "user_id" }
      );
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(["mobile-layout", user?.id], next);
    },
  });

  const resetLayout = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not signed in");
      const { error } = await supabase.from("user_mobile_layouts").delete().eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData(["mobile-layout", user?.id], DEFAULT_LAYOUT);
    },
  });

  const allowed = (moduleKey?: string) => !moduleKey || isModuleEnabled(moduleKey);

  /** Resolved, module-filtered entries ready for rendering. */
  const resolved = useMemo(() => {
    const shortcuts = layout.shortcuts
      .map(shortcutByKey)
      .filter((s): s is MobileShortcut => !!s && allowed(s.moduleKey));
    const sections = layout.sections
      .map(sectionByKey)
      .filter((s): s is MobileSection => !!s && allowed(s.moduleKey));
    const tabs = layout.bottomTabs
      .map(tabByKey)
      .filter((t): t is MobileTab => !!t && allowed(t.moduleKey))
      .slice(0, MAX_CONFIGURABLE_TABS);
    const more = MOBILE_TABS.find((t) => t.fixed)!;
    return { shortcuts, sections, tabs: [...tabs, more] };
  }, [layout, isModuleEnabled]);

  return {
    layout,
    resolved,
    isLoading: query.isLoading || modulesLoading,
    saveLayout,
    resetLayout,
    isModuleEnabled,
  };
}
